import React, { useState, useEffect, useRef } from 'react';
import { vendutoApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, Save, Download, Upload, Database, Armchair, AlertTriangle, Sun, Snowflake, Mail, Send,
} from 'lucide-react';
import VendutoHeader from '../components/venduto/VendutoHeader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { fmtNum, fmtEuro, formatData, MESI, anniDisponibili } from '../lib/venduto';

const Riga = ({ label, valore, testid }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="font-semibold tabular-nums" data-testid={testid}>{valore}</p>
  </div>
);

const VendutoImpostazioniPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin' || user?.is_super_admin;
  const restoreRef = useRef(null);

  const [stato, setStato] = useState(null);
  const [capInvernale, setCapInvernale] = useState('');
  const [capEstiva, setCapEstiva] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [scaricando, setScaricando] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreInfo, setRestoreInfo] = useState(null);
  const [ispezionando, setIspezionando] = useState(false);
  const [confermato, setConfermato] = useState(false);
  const [ripristinando, setRipristinando] = useState(false);

  // Report mensile: default al mese precedente
  const oggi = new Date();
  const mesePrec = oggi.getMonth() === 0 ? 12 : oggi.getMonth();
  const annoPrec = oggi.getMonth() === 0 ? oggi.getFullYear() - 1 : oggi.getFullYear();
  const [reportMese, setReportMese] = useState(mesePrec);
  const [reportAnno, setReportAnno] = useState(annoPrec);
  const [inviando, setInviando] = useState(false);
  const anniReport = anniDisponibili(stato);

  const inviaReport = async () => {
    setInviando(true);
    try {
      const res = await vendutoApi.inviaReportMensile(reportAnno, reportMese);
      toast.success(`Report di ${MESI[reportMese - 1]} ${reportAnno} inviato a ${res.destinatario}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invio del report non riuscito');
    } finally {
      setInviando(false);
    }
  };

  const carica = async () => {
    try {
      const s = await vendutoApi.getStato();
      setStato(s);
      setCapInvernale(String(s.capienza.invernale));
      setCapEstiva(String(s.capienza.estiva));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento');
    }
  };

  useEffect(() => { carica(); }, []);

  const salvaCapienze = async () => {
    setSalvando(true);
    try {
      await vendutoApi.saveConfig(Number(capInvernale), Number(capEstiva));
      toast.success('Capienze aggiornate. I nuovi consolidamenti useranno questi valori.');
      carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSalvando(false);
    }
  };

  const scaricaBackup = async () => {
    setScaricando(true);
    try {
      const blob = await vendutoApi.downloadBackup();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `venduto_backup_${new Date().toISOString().slice(0, 10)}.json.gz`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Backup scaricato (file compresso .json.gz)');
    } catch (e) {
      toast.error('Errore nel download del backup');
    } finally {
      setScaricando(false);
    }
  };

  const chiudiRestore = () => {
    setRestoreFile(null);
    setRestoreInfo(null);
    setConfermato(false);
    if (restoreRef.current) restoreRef.current.value = '';
  };

  const scegliFile = async (file) => {
    if (!file) return;
    setRestoreFile(file);
    setRestoreInfo(null);
    setConfermato(false);
    setIspezionando(true);
    try {
      setRestoreInfo(await vendutoApi.inspectBackupFile(file));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'File di backup non valido');
      chiudiRestore();
    } finally {
      setIspezionando(false);
    }
  };

  const ripristina = async () => {
    if (!restoreFile) return;
    setRipristinando(true);
    try {
      const res = await vendutoApi.restoreBackupFile(restoreFile);
      toast.success(`Archivio ripristinato: ${res.giorni} righe giornata, ${res.righe_prodotto} righe prodotto`);
      chiudiRestore();
      carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'File di backup non valido');
    } finally {
      setRipristinando(false);
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <VendutoHeader titolo="Setup venduto" sottotitolo="Capienza, archivio e backup" />

      <main className="px-4 py-5 max-w-lg mx-auto space-y-4">
        {/* Stato archivio */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="w-4 h-4 text-primary" /> Stato archivio
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm" data-testid="stato-archivio">
            {!stato ? (
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            ) : (
              <>
                <p className="flex justify-between">
                  <span className="text-muted-foreground">Periodo coperto</span>
                  <span className="font-mono-numbers">
                    {stato.data_min ? `${formatData(stato.data_min)} → ${formatData(stato.data_max)}` : '—'}
                  </span>
                </p>
                <p className="flex justify-between">
                  <span className="text-muted-foreground">Giornate in archivio</span>
                  <span className="font-mono-numbers">{fmtNum(stato.n_giorni)}</span>
                </p>
                <p className="flex justify-between">
                  <span className="text-muted-foreground">Righe prodotto</span>
                  <span className="font-mono-numbers">{fmtNum(stato.n_righe_prodotto)}</span>
                </p>
                {stato.ultimo_consolidamento && (
                  <div className="mt-3 p-3 rounded-lg bg-secondary text-xs space-y-1">
                    <p className="font-semibold">Ultimo consolidamento</p>
                    <p className="font-mono-numbers">
                      {new Date(stato.ultimo_consolidamento.created_at).toLocaleString('it-IT')}
                    </p>
                    <p>
                      {stato.ultimo_consolidamento.giorni_nuovi} giorni nuovi,{' '}
                      {stato.ultimo_consolidamento.giorni_sostituiti} sostituiti ·{' '}
                      {fmtNum(stato.ultimo_consolidamento.n_documenti)} documenti
                    </p>
                    <p className="text-muted-foreground">
                      {(stato.ultimo_consolidamento.files || []).map((f) => `${f.filename} (${f.tipo})`).join(', ')}
                    </p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Capienza stagionale */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Armchair className="w-4 h-4 text-primary" /> Capienza stagionale
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Da giugno a settembre si usa la capienza estiva, negli altri mesi quella invernale.
              La capienza viene registrata su ogni giornata al momento del consolidamento.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">
                  <Snowflake className="w-3 h-3" /> Invernale
                </Label>
                <Input
                  type="number"
                  value={capInvernale}
                  onChange={(e) => setCapInvernale(e.target.value)}
                  className="h-12 font-mono-numbers"
                  disabled={!isAdmin}
                  data-testid="capienza-invernale-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1">
                  <Sun className="w-3 h-3" /> Estiva (giu-set)
                </Label>
                <Input
                  type="number"
                  value={capEstiva}
                  onChange={(e) => setCapEstiva(e.target.value)}
                  className="h-12 font-mono-numbers"
                  disabled={!isAdmin}
                  data-testid="capienza-estiva-input"
                />
              </div>
            </div>
            {isAdmin && (
              <Button
                className="w-full h-12"
                onClick={salvaCapienze}
                disabled={salvando || !capInvernale || !capEstiva}
                data-testid="salva-capienze-btn"
              >
                {salvando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Salva capienze
              </Button>
            )}
            {!isAdmin && (
              <p className="text-xs text-muted-foreground italic">
                Solo l'amministratore può modificare le capienze.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Report mensile automatico */}
        {isAdmin && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" /> Report mensile via email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Ogni <strong>primo del mese</strong> ricevi via email il report del mese appena chiuso:
                metriche per servizio, riempimento della sala, composizione dell'incasso e top 10 prodotti.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Select value={String(reportMese)} onValueChange={(v) => setReportMese(Number(v))}>
                  <SelectTrigger className="h-11" data-testid="report-mese-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MESI.map((m, i) => (
                      <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(reportAnno)} onValueChange={(v) => setReportAnno(Number(v))}>
                  <SelectTrigger className="h-11" data-testid="report-anno-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {anniReport.map((a) => (
                      <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                className="w-full h-12"
                onClick={inviaReport}
                disabled={inviando}
                data-testid="invia-report-btn"
              >
                {inviando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Invia ora il report del mese scelto
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Backup / Restore */}
        {isAdmin && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Backup dell'archivio</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Esporta tutto l'archivio del venduto in un file compresso (.json.gz) e reimportalo
                quando serve: è il modo più rapido per portare l'archivio in produzione dopo un
                aggiornamento. Il ripristino sostituisce integralmente l'archivio del locale attivo.
              </p>
              <Button
                variant="outline"
                className="w-full h-12"
                onClick={scaricaBackup}
                disabled={scaricando}
                data-testid="scarica-backup-btn"
              >
                {scaricando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Esporta backup archivio
              </Button>

              <input
                ref={restoreRef}
                type="file"
                accept=".json,.gz"
                className="hidden"
                onChange={(e) => scegliFile(e.target.files?.[0])}
                data-testid="restore-file-input"
              />
              <Button
                variant="outline"
                className="w-full h-12"
                onClick={() => restoreRef.current?.click()}
                data-testid="scegli-restore-btn"
              >
                <Upload className="w-4 h-4 mr-2" /> Reimporta backup archivio
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      <Dialog open={!!restoreFile} onOpenChange={(o) => { if (!o) chiudiRestore(); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ripristinare l'archivio?</DialogTitle>
            <DialogDescription>
              Controlla il contenuto del file: il ripristino sostituisce integralmente l'archivio
              del venduto di questo locale.
            </DialogDescription>
          </DialogHeader>

          {ispezionando && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4" data-testid="restore-ispezione">
              <Loader2 className="w-4 h-4 animate-spin" /> Lettura del file in corso…
            </div>
          )}

          {restoreInfo && (
            <div className="space-y-3" data-testid="restore-riepilogo">
              <div className="rounded-lg border p-3 space-y-1.5">
                <p className="text-xs text-muted-foreground break-all">
                  File: <strong className="text-foreground">{restoreFile?.name}</strong>
                  {' · '}{(restoreFile?.size / 1048576).toFixed(1)} MB
                  {restoreInfo.exported_at ? ` · esportato il ${formatData(restoreInfo.exported_at.slice(0, 10))}` : ''}
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm pt-1">
                  <Riga label="Periodo" valore={`${formatData(restoreInfo.data_min)} → ${formatData(restoreInfo.data_max)}`} testid="info-periodo" />
                  <Riga label="Giornate" valore={fmtNum(restoreInfo.n_giorni)} testid="info-giornate" />
                  <Riga label="Documenti" valore={fmtNum(restoreInfo.documenti)} testid="info-documenti" />
                  <Riga label="Coperti" valore={fmtNum(restoreInfo.coperti)} testid="info-coperti" />
                  <Riga label="Incasso totale" valore={fmtEuro(restoreInfo.incasso, 2)} testid="info-incasso" />
                  <Riga label="Righe prodotto" valore={fmtNum(restoreInfo.righe_prodotto)} testid="info-righe" />
                </div>
                {restoreInfo.tipi?.length > 0 && (
                  <p className="text-xs text-muted-foreground pt-1">
                    Tipi documento nel file: {restoreInfo.tipi.join(', ')}
                  </p>
                )}
              </div>

              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <p>
                  L'archivio attuale di questo locale{' '}
                  {restoreInfo.attuale?.vuoto
                    ? 'è vuoto: verrà popolato con i dati del file.'
                    : `(${fmtNum(restoreInfo.attuale?.n_giorni)} giornate, ${formatData(restoreInfo.attuale?.data_min)} → ${formatData(restoreInfo.attuale?.data_max)}) verrà cancellato e sostituito.`}
                  {' '}Chiusure di cassa, uscite e bilancio non vengono toccati.
                  {!restoreInfo.stesso_locale && ' Attenzione: il file è stato esportato da un altro locale o da un altro ambiente (es. preview).'}
                </p>
              </div>

              <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="mt-0.5 w-4 h-4 accent-red-600"
                  checked={confermato}
                  onChange={(e) => setConfermato(e.target.checked)}
                  data-testid="restore-conferma-checkbox"
                />
                <span>Ho controllato i dati e confermo la sostituzione dell'archivio.</span>
              </label>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={chiudiRestore} data-testid="annulla-restore-btn">Annulla</Button>
            <Button
              onClick={ripristina}
              disabled={ripristinando || !restoreInfo || !confermato}
              data-testid="conferma-restore-btn"
            >
              {ripristinando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Ripristina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendutoImpostazioniPage;
